/**
 * The kitchen that is already in the room, read back out of a scan.
 *
 * Scanning an empty room and getting a rectangle is correct. Scanning a kitchen you are
 * about to renovate and getting a rectangle is not: RoomPlan found the cupboards, the sink,
 * the cooktop, the fridge and the doorway, and the app asked for none of it.
 *
 * WHAT THE FIRST VERSION GOT WRONG, from a real scan of a real kitchen:
 *
 *   It handed each wall's scanned cabinets to `refit`, which answers a different question.
 *   `refit` means "the wall changed length, re-cut the run": it drops items from the END
 *   when there is too much, and refills what is left with the WIDEST cabinet in the range.
 *   So a scan that measured 4.5m of cupboards against a 4.2m wall lost the fridge and the
 *   oven at the far end, and what came back was a row of identical 900 drawer units.
 *
 *   It only made an overhead run where it had FOUND overheads -- and a scan under a
 *   bulkhead and a rangehood routinely misses the top row. A wall with no overhead run has
 *   nowhere to put cupboards and no way to add them.
 *
 *   It mentioned an island in a note and did not make one.
 *
 * So now every scanned thing keeps its measured POSITION and role, and the gaps between
 * them are filled -- which is how `design.js` lays out a leg around a locked cabinet.
 * Positions and roles are what a scan is FOR; the filling is what makes the run add up.
 *
 * WHERE A RUN'S LENGTH COMES FROM, after the second real scan.
 *
 *   RoomPlan's classifier is good at appliances -- its own categories include `sink`,
 *   `stove`, `oven`, `dishwasher` and `refrigerator` -- and poor at fitted cabinetry. A
 *   continuous run of base cupboards under a benchtop comes back as one or two
 *   low-confidence `storage` boxes, or as nothing at all. Counting boxes was therefore the
 *   wrong question, and it is why a kitchen full of cupboards came back empty.
 *
 *   So the RUN comes from geometry and the FIXED POINTS come from the classifier. A
 *   benchtop is a horizontal surface 800-1000 above the floor, and its extent along a wall
 *   IS the base run's extent on that wall whether or not a single door was recognised. An
 *   overhead run shows as vertical surfaces 1300-2300 high standing a little off the wall.
 *   Those arrive as `scan.benches` and `scan.fronts` (see the RoomPlan plugin), and each
 *   becomes a run with a `span`. The appliances are then laid into that span at the
 *   positions they were measured at, and the maker's own range fills between them.
 *
 * WHEN IT DECLINES, and what it will no longer invent.
 *
 *   No bench, and fewer than MIN_OBJECTS usable objects: no runs. An empty room is right
 *   for a new build, and a wrong guess costs somebody a deletion before they can start.
 *
 *   And a run is only ever made where there is EVIDENCE of one. This app used to put an
 *   overhead run above every base run "for you", which the scene then filled -- so a wall
 *   carrying an air-conditioner and nothing else was drawn with a row of cupboards, and
 *   the owner's words for that were "cupboards made up where they don't exist". A wall
 *   with no overhead evidence now gets no overhead run, and the notes say so; putting one
 *   up is one tap on the wall.
 *
 * Pure. Segments in, plan pieces out. No Capacitor, no three.js, no Vue.
 */
import * as room from './room.js'
import { dirToPlan, toPlan } from './roomplan.js'
import { fill, sum } from './layout.js'

/**
 * RoomPlan's own category names, and what each is in a kitchen.
 *
 * Matched LOOSELY, because the Swift side sends `String(describing:)` of an enum and Apple
 * has changed that shape before: `storage`, `Category.storage` and `storage(type: .cabinet)`
 * are the same thing, and an exact-match table silently drops the two spellings it does not
 * know -- which is one candidate for the missing sink and oven in the owner's scan.
 */
const ROLE = {
  storage: null,            // cabinetry: the cabinet is chosen by size, not by role
  cabinet: null,
  sink: 'sink',
  stove: 'cooktop',
  cooktop: 'cooktop',
  oven: 'oven',
  dishwasher: 'dishwasher',
  refrigerator: 'fridge',
  fridge: 'fridge',
}

/** "Category.storage(type: .cabinet)" -> "storage". Apple's spelling, reduced to the word. */
const categoryOf = (category) => {
  const raw = String(category ?? '').toLowerCase()
  return (raw.split('(')[0].split('.').pop() ?? '').trim()
}

/** The kitchen word for a scanned category, or undefined when it is not kitchen at all. */
export function roleOf(category) {
  const name = categoryOf(category)
  return Object.prototype.hasOwnProperty.call(ROLE, name) ? ROLE[name] : undefined
}

/** Below this many cabinets it is a room with furniture in it, not a kitchen. */
const MIN_OBJECTS = 3
/** How far from a wall an object can be and still be against it. */
const NEAR_WALL = 900
/** The same, for a doorway: it belongs to a wall even when the wall was scanned in pieces. */
const NEAR_OPENING = 1800
/** Anything shorter than this is a scan artefact, not a cabinet. */
const MIN_WIDTH = 250
/** Above the floor by more than this and it is an overhead, not a base unit. */
const OVERHEAD_AT = 1250
/** Taller than this, standing on the floor, and it is a pantry or a fridge. */
const TALL_AT = 1400
/** An island has to be at least this long to be worth making. */
const MIN_ISLAND = 1200

/**
 * Two stretches of measured bench closer together than this are one bench.
 *
 * A scan of a benchtop is not one clean rectangle: a hob, a sink cut-out, a kettle and the
 * shadow under an overhead all leave holes in it. A hole this size is a hole in the SCAN,
 * not a break in the cabinetry, and joining them is what turns four fragments into the one
 * run a person would point at.
 */
const BENCH_JOIN = 300

/**
 * Heights a benchtop patch may honestly be at, in millimetres above the floor.
 *
 * Deliberately wider than the 800-1000 band the plugin already applied. The tight band is
 * the Swift side's decision and it is made once, where the geometry is; this only refuses a
 * floor or a ceiling that leaked through a future change at the other end. A patch with no
 * height at all is trusted, because a caller that omits it is a test or an older payload.
 */
const BENCH_BAND = [600, 1200]

/**
 * WHAT A KITCHEN CUPBOARD IS, VERTICALLY, and what a television unit is not.
 *
 * Measured ABOVE THE FLOOR (see `floorOf` -- the scan does not count from there). A kitchen
 * is built to two heights and only two: a benchtop, and full height. Everything else in a
 * house is furniture. A sideboard and a TV unit are 650-700 tall, which is below a bench and
 * above nothing, and that is the whole of what separates them from cabinetry in a scan that
 * calls all four of them `storage`.
 *
 *   BENCH_TOP  the top of a base cupboard: a bench, plus or minus whatever the LiDAR made
 *              of the kettle standing on it
 *   TALL_TOP   a pantry or an oven tower, which reaches past every piece of furniture
 *   OVERHEAD_AT (above) is the third: a box that starts up in the overhead band
 */
const BENCH_TOP = [800, 1200]
const TALL_TOP = 1400

const near = (v, list) => list.reduce((best, x) => (Math.abs(x - v) < Math.abs(best - v) ? x : best), list[0])

/** A length in millimetres, grouped, for a sentence somebody reads: 3420 -> "3,420". */
const mm = (v) => Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

/** "the back wall, the left wall and the island", for a note about several of them. */
const listOf = (words) => (words.length <= 1 ? (words[0] ?? '')
  : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`)

/**
 * INTO THE PLANNER'S FRAME, every scanned thing, through the one transform.
 *
 * `loopFromSegments` turns the room so its longest wall lies along +x and then re-origins it
 * twice, and everything else the scan found is still where the phone put it -- so without
 * this a door on the front wall is laid against the back one, and with only the translation
 * (which is all this used to do) the whole room's contents stay at the phone's heading while
 * the walls come out square. A DIRECTION turns and does not move; a POINT does both.
 * Benchtops, cupboard fronts and the island all carry a direction, and a bench left at forty
 * degrees to the wall it sits on measures a run of the wrong length.
 *
 * Lifted out of `fromScan` when `kitchenWalls` came along, because the two of them have to
 * agree about where everything is to the millimetre: a wall ticked by one and laid out by
 * the other from a different frame is a wall with cabinets on it that the person never chose.
 */
function intoPlan(scan, origin) {
  if (!scan) return scan
  const here = (o) => {
    const p = toPlan(origin, o.cx, o.cz)
    const out = { ...o, cx: p.x, cz: p.y }
    if (o.dx !== undefined || o.dz !== undefined) {
      const d = dirToPlan(origin, o.dx ?? 0, o.dz ?? 0)
      out.dx = d.x
      out.dz = d.y
    }
    return out
  }
  return {
    ...scan,
    objects: (scan.objects ?? []).map(here),
    doors: (scan.doors ?? []).map(here),
    windows: (scan.windows ?? []).map(here),
    openings: (scan.openings ?? []).map(here),
    // The measured surfaces travel with everything else. They are in the phone's frame
    // exactly as the objects are, and a bench left behind lands against the wrong wall.
    benches: (scan.benches ?? []).map(here),
    fronts: (scan.fronts ?? []).map(here),
  }
}

/**
 * What the scan found, as runs the planner can hold.
 *
 * @param {{ objects?: Array, doors?: Array, windows?: Array, openings?: Array,
 *           benches?: Array, fronts?: Array }} scan
 * @param {object} maker
 * @param {Array} walls  the loop already built by roomplan.loopFromSegments
 * @param {{x, y, angle}} origin  the frame that loop put the room in -- the turn that
 *        squared it up and the two re-originings -- so the rest of the scan can follow it.
 *        See `roomplan.toPlan`; a caller with numbers already in the planner's frame passes
 *        nothing and gets the identity.
 * @param {{ only?: Iterable<string> }} [opts]
 *        `only` is WHICH SEGMENTS MAY CARRY CABINETS -- the walls a person ticked on the
 *        "which walls are the kitchen?" step, and the island if they kept it. See below.
 * @returns {{ runs, islands, openings, notes: string[], counted: number, saw, services }}
 */
export function fromScan(scan, maker, walls, origin = { x: 0, y: 0 }, { only } = {}) {
  const notes = []
  const lim = maker.limits
  /*
   * WHICH SEGMENTS ARE ALLOWED A RUN AT ALL.
   *
   * A scan of an open-plan floor is a scan of the kitchen AND the dining table AND the
   * lounge, and it is all equally true -- the owner's came back as twenty walls in one
   * closed loop. Nothing in the geometry says where the kitchen stops, because nothing in
   * the room does either; only the person who lives there knows, and now they are asked.
   *
   * `only` is purely SUBTRACTIVE: evidence still decides whether a wall gets a run and what
   * kind, exactly as it always did, and this then keeps the ones that were chosen. A caller
   * that passes nothing gets what it got before, to the millimetre.
   *
   * The walls left out keep their doorways, their windows and their geometry. They are
   * still the room -- see `openings` below, which is built from all of them -- they simply
   * have nothing standing against them.
   */
  const allowed = only ? new Set(only) : null

  scan = intoPlan(scan, origin)

  const openings = readOpenings(scan, walls)

  /*
   * WHAT APPLE ACTUALLY HANDED BACK, kept and reported.
   *
   * This used to drop everything it did not recognise in silence, and then return no runs
   * in silence too -- the notes explaining why were only ever shown when there WERE runs,
   * so a scan of a kitchen full of cupboards came back as an empty room with nothing said.
   * There is no way to debug that from the other end of a phone. `saw` is the whole of what
   * came in, `unknown` is every category name we did not recognise, and both go out with
   * the result whether it found a kitchen or not.
   *
   * `things` is every object one line each, category, confidence and width. The counts
   * alone were not enough to read the owner's scan from here: "20 objects, 14 cabinets"
   * cannot tell you that the oven came back low-confidence and was dropped, and that is
   * exactly what had happened.
   */
  const all = (scan?.objects ?? []).filter(Boolean)

  /*
   * THE ISLAND, WHICH CAME BACK AS A TABLE.
   *
   * A big benchtop with stools under it is a `table` to RoomPlan, and `table` is not in
   * ROLE, so the owner's island was counted as "a kind of thing this app does not fit out"
   * and thrown away. Standing away from every wall and longer than MIN_ISLAND is not
   * furniture: nothing else in a kitchen is shaped like that. A small table near a wall is
   * still ignored, which is the whole reason the two tests are on length and distance
   * rather than on the word.
   */
  const ISLAND_CATEGORY = new Set(['table', 'storage', 'cabinet'])
  const islandObjects = all.filter((o) => ISLAND_CATEGORY.has(categoryOf(o.category))
    && (o.length ?? 0) >= MIN_ISLAND && (nearestWall(o, walls)?.gap ?? Infinity) > NEAR_WALL)
  const usedForIsland = new Set(islandObjects.map((o) => String(o.category ?? '?')))

  const unknown = [...new Set(all.filter((o) => roleOf(o.category) === undefined)
    .map((o) => String(o.category ?? '?')).filter((c) => !usedForIsland.has(c)))]
  const saw = {
    walls: walls.length,
    doors: (scan?.doors ?? []).length,
    windows: (scan?.windows ?? []).length,
    openings: (scan?.openings ?? []).length,
    objects: all.length,
    kinds: tally(all.map((o) => String(o.category ?? '?'))),
    unknown,
    usedOpenings: openings.length,
    benches: (scan?.benches ?? []).length,
    fronts: (scan?.fronts ?? []).length,
    things: all.map((o) => ({
      category: String(o.category ?? '?'),
      confidence: String(o.confidence ?? '?'),
      length: Math.round(o.length ?? 0),
    })),
    /*
     * AND THE GEOMETRY, so a scan that goes wrong in somebody's kitchen can be reproduced
     * in a test here.
     *
     * "Copy what the scan saw" is how a bug travels from a phone to this codebase, and until
     * now it carried counts and category names and no numbers at all. Every real bug in this
     * file has turned out to be about WHERE something is -- a doorway matched to the wrong
     * wall, a bench measured past a corner, a room at forty degrees -- and none of that is
     * visible in "18 objects, 12 cabinets". A paste with the geometry in it becomes a
     * fixture in one step (`scripts/scan-fixture.mjs`), and then it is a test.
     *
     * Millimetres, in the PLANNER's frame: the room already squared up and re-origined, so
     * what is pasted is what this file actually worked on rather than what the phone
     * happened to be pointing at.
     */
    geometry: geometryOf(scan, walls, openings),
  }

  /*
   * WHAT SURVIVES A LOW-CONFIDENCE READING, and what does not.
   *
   * The gate used to be "high or medium, whatever it is". In the owner's kitchen RoomPlan's
   * own live view drew a box around the oven and around the overhead cabinets and we lost
   * both -- and a fixed point in roughly the right place is worth incomparably more than no
   * fixed point at all, because the whole design hangs off where the sink, the cooktop, the
   * oven, the dishwasher and the fridge ARE. A plain `storage` box is the opposite: it has
   * no identity to be right about, only a width, and an uncertain one is noise.
   *
   * So the confidence gate applies to cabinetry only. MIN_WIDTH applies to everything: a
   * 90mm "cabinet" is an artefact whatever the classifier thinks of it.
   */
  const objects = all.filter((o) => roleOf(o.category) !== undefined)
  const usable = objects.filter((o) => o.length >= MIN_WIDTH
    && (roleOf(o.category) ? true : o.confidence !== 'low'))
  if (unknown.length) {
    notes.push(`The scan also found ${unknown.length} kind${unknown.length > 1 ? 's' : ''} of `
      + `thing this app does not fit out: ${unknown.join(', ')}.`)
  }
  const shaky = usable.filter((o) => o.confidence === 'low')
  if (shaky.length) {
    notes.push(`The scan was unsure of ${shaky.length} of the appliance${shaky.length > 1 ? 's' : ''} `
      + 'it found. They were kept where they were measured — an oven in roughly the right place '
      + 'is worth more than no oven — so check them first.')
  }
  if (objects.length && !usable.length) notes.push('What the scan found was too uncertain to use.')

  /*
   * THE RUNS THE SCAN MEASURED, before a single cabinet has been named.
   *
   * A benchtop's extent along its wall is the base run's extent. A row of cupboard fronts
   * up in the overhead band is the overhead run's. Both are intervals in the wall's own
   * millimetres and both become `run.span`, which is the existing mechanism for a run that
   * does not fill its whole wall (`room.extents` clamps it into what the corners and the
   * doorways allow, so a bench that reads as running into the corner still gives the corner
   * unit its return leg).
   */
  const benches = (scan?.benches ?? []).filter((b) => b && (b.length ?? 0) > 0
    && (!Number.isFinite(b.y) || (b.y >= BENCH_BAND[0] && b.y <= BENCH_BAND[1])))
  const fronts = (scan?.fronts ?? []).filter((f) => f && (f.length ?? 0) > 0
    && (f.y1 ?? Infinity) > OVERHEAD_AT)
  const bench = alongWalls(benches, walls)
  const front = alongWalls(fronts, walls)
  const measuredWalls = new Set([...bench.spans.keys(), ...front.spans.keys()])

  /*
   * WHEN IT GIVES UP. Fewer than MIN_OBJECTS usable objects AND no measured bench: there is
   * nothing here to reproduce. The bench is what changed -- a wall whose benchtop was
   * measured has a run on it whatever the classifier managed to name, which is the whole
   * point of measuring it -- but a scan with no geometry at all behaves exactly as before.
   */
  if (usable.length < MIN_OBJECTS && !measuredWalls.size) {
    notes.unshift(all.length
      ? `The scan found ${all.length} thing${all.length > 1 ? 's' : ''} in the room but only `
        + `${usable.length} that could be a cabinet, so the room was left empty.`
      : 'The scan found the walls but nothing standing in the room, so it was left empty.')
    return { runs: [], islands: [], openings, counted: usable.length, notes, saw, services: [] }
  }

  // Every object, put against the wall it is nearest and measured along it.
  const placed = []
  for (const o of usable) {
    const hit = nearestWall(o, walls)
    if (!hit) continue
    placed.push({ ...o, wallId: hit.wall.id, at: hit.at, gap: hit.gap, role: roleOf(o.category) })
  }
  const against = placed.filter((p) => p.gap <= NEAR_WALL)
  const adrift = placed.filter((p) => p.gap > NEAR_WALL)

  /*
   * The island: whatever was standing away from the walls, MADE rather than mentioned.
   *
   * Three witnesses, best first: a benchtop patch measured out in the middle of the room, a
   * single big object the classifier called a table or a cupboard, and failing both, the
   * bounding box of whatever small things were adrift. The first two carry a real length
   * and a real direction; the last only has centres and has to guess how far the end ones
   * reach.
   */
  const islands = []
  /*
   * WHICH big adrift thing is the island, when more than one qualifies.
   *
   * The owner's scan returned three `table` objects. One is the island; one is the dining
   * table on the other side of an open-plan room; the third is anybody's guess. They are
   * the same shape, the same category and the same confidence, so nothing about the object
   * itself separates them -- the only thing that does is that an island is part of the
   * kitchen and a dining table is across the room from it. `kitchenAt` is where the
   * cabinetry actually is, and the nearest candidate to it wins.
   */
  const kitchenAt = against.length ? against.map((p) => ({ cx: p.cx, cz: p.cz }))
    : [...bench.spans.keys()].map((id) => {
      const w = walls.find((x) => x.id === id)
      const mid = room.at(w, room.length(w) / 2)
      return { cx: mid.x, cz: mid.y }
    })
  const isle = islandFrom(adrift, [
    ...bench.adrift.map((p) => ({ ...p, fromBench: true })),
    ...islandObjects,
  ], kitchenAt)
  if (isle) {
    islands.push(isle.segment)
    notes.push(isle.measured
      ? `An island ${mm(isle.length)} long, from what the scan measured standing away from `
        + 'the walls. Drag it to line it up.'
      : `An island ${Math.round(isle.length)}mm long, from ${adrift.length} `
        + `thing${adrift.length > 1 ? 's' : ''} standing away from the walls. Drag it to line it up.`)
    if (isle.others) {
      notes.push(`${isle.others} other big thing${isle.others > 1 ? 's were' : ' was'} standing `
        + 'away from the walls — a dining table, most likely. The one nearest the cabinets was '
        + 'made into the island and the rest were left out.')
    }
  } else if (adrift.length) {
    notes.push(`${adrift.length} thing${adrift.length > 1 ? 's were' : ' was'} away from the walls `
      + 'and too small to be an island, so they were left out.')
  }

  if (against.length < MIN_OBJECTS && !isle && !measuredWalls.size) {
    notes.unshift(`${placed.length} thing${placed.length > 1 ? 's were' : ' was'} found but only `
      + `${against.length} against a wall, so the room was left empty.`)
    return { runs: [], islands: [], openings, counted: against.length, notes, saw, services: [] }
  }

  const runs = []
  const stamp = { n: 0 }
  const plan = { walls, islands, openings, runs }

  /*
   * WHICH RUNS THE SCAN ITSELF MEASURED.
   *
   * A run in here has an extent that came off a surface rather than out of a range, so the
   * maker's cabinets are cut to fit it even when the classifier named nothing on that wall
   * at all. It is also what says "ignore the plain `storage` boxes here": a measured bench
   * is a better witness to where the cupboards stop than a low-confidence box is, and a run
   * that has both should not be pulled about by the worse of the two.
   */
  const measured = new Set()

  /*
   * AND THE APPLIANCES REACH PAST THE BENCH.
   *
   * A fridge is not under the benchtop and neither is a full-height oven tower, so a run
   * cut to the bench alone stops short of them and `layOut` then shoves them back inside
   * it, on top of a cupboard. The span grows to cover any appliance the scan put on that
   * wall; `room.extents` still clamps the result into the wall, the corner and the doors.
   */
  for (const p of against) {
    const span = bench.spans.get(p.wallId)
    if (!span || !p.role || kindOf(p) !== 'base') continue
    const w = walls.find((x) => x.id === p.wallId)
    const half = (p.length ?? 0) / 2
    span.from = Math.max(0, Math.min(span.from, p.at - half))
    span.to = Math.min(room.length(w), Math.max(span.to, p.at + half))
  }

  /*
   * A RUN ONLY WHERE THERE IS EVIDENCE OF ONE.
   *
   * Base: a measured benchtop, or something standing on the floor against that wall.
   * Overhead: measured fronts, or something the classifier found up in the overhead band.
   *
   * The overhead half is the change. Every wall with base cabinets used to get an empty
   * overhead run "for you", and an empty run is filled by the scene's `compose` -- so the
   * app drew a row of cupboards along a wall that has an air-conditioner on it. There is no
   * honest way to tell the difference between "the bulkhead hid them" and "there are none"
   * from a scan that saw none, and inventing them is the answer that cannot be undone
   * without noticing it first. So the wall is left bare and the note says which walls and
   * why; tapping the wall puts a row up.
   */
  const based = (w) => bench.spans.has(w.id)
    || against.some((p) => p.wallId === w.id && kindOf(p) === 'base')
  const topped = (w) => front.spans.has(w.id)
    || against.some((p) => p.wallId === w.id && kindOf(p) === 'wall')
  for (const w of walls) {
    // Not the kitchen, said by the person whose kitchen it is. The evidence below is not
    // re-argued with: a benchtop against the lounge wall is still a benchtop, it is just
    // not one they want cupboards under.
    if (allowed && !allowed.has(w.id)) continue
    for (const kind of ['base', 'wall']) {
      if (!(kind === 'base' ? based(w) : topped(w))) continue
      const span = (kind === 'base' ? bench : front).spans.get(w.id)
      const run = { id: `r-${w.id}-${kind}-s${stamp.n++}`, wallId: w.id, kind, items: [] }
      if (span) {
        run.span = { from: Math.round(span.from), to: Math.round(span.to) }
        measured.add(run.id)
      }
      runs.push(run)
    }
  }
  // The island keeps its FOOTPRINT either way -- it is where the family walks around and
  // where the slab and the power under the floor already are, which is a fact about the
  // room. What the tick decides is whether anything is built on it.
  if (isle && (!allowed || allowed.has(isle.segment.id))) {
    const run = { id: `r-isle-base-s${stamp.n++}`, wallId: isle.segment.id, kind: 'base', items: [] }
    // An island's extent is the segment itself, so it needs no span -- but its length was
    // measured all the same, and the range fills it whether or not anything was named on it.
    if (isle.measured) measured.add(run.id)
    runs.push(run)
  }

  // Now fill each one around what the scan actually found on it.
  const ext = room.extents(plan, lim)
  for (const run of runs) {
    const onIsland = run.wallId === isle?.segment.id
    const seg = room.segment(plan, run.wallId)
    const { from, to } = ext[run.id] ?? { from: 0, to: seg ? room.length(seg) : 0 }
    const mine = (onIsland ? adrift : against.filter((p) => p.wallId === run.wallId))
      .filter((p) => kindOf(p) === run.kind)
      // On a measured run the plain cupboards are noise: the bench says where the run
      // starts and stops, the appliances say what is fixed inside it, and the range fills
      // the rest. Elsewhere a `storage` box is still the only evidence there is.
      .filter((p) => !measured.has(run.id) || !!p.role)
    const built = layOut(mine, from, to, run, maker, seg, { plan, measured: measured.has(run.id) })
    run.items = built.items
    const where = wallName(run.wallId, onIsland, walls)
    if (measured.has(run.id) && to - from > 0.5) {
      notes.push(`${where}'s run is ${mm(to - from)} from the `
        + `${run.kind === 'base' ? 'benchtop' : 'cupboard fronts'} the scan measured; the `
        + 'cupboards in it are from the range.')
    }
    if (built.problem) notes.push(`${where}: ${built.problem}`)
    if (built.dropped) {
      notes.push(`${where}: the scan measured ${built.dropped} cabinet`
        + `${built.dropped > 1 ? 's' : ''} more than the wall has room for, so the plain `
        + 'cupboards went. The sink and the appliances stayed.')
    }
  }

  const cabinets = runs.reduce((n, r) => n + r.items.filter((i) => i.specId && !i.gap && !i.filler).length, 0)
  const roles = [...new Set(placed.map((p) => p.role).filter(Boolean))]
  notes.unshift(`${cabinets} cabinets${roles.length ? `, and the ${roles.join(', ')}` : ''}, `
    + 'measured from the scan. Check every one against a tape.')

  // Which walls got cabinets but no row above them, and why. Said plainly, because the
  // alternative -- putting the row up anyway -- is what drew cupboards along a wall that
  // has none.
  const bare = walls.filter((w) => runs.some((r) => r.wallId === w.id && r.kind === 'base')
    && !runs.some((r) => r.wallId === w.id && r.kind === 'wall'))
  if (bare.length) {
    notes.push(`${listOf(bare.map((w) => wallName(w.id, false, walls)))} got no overhead cupboards: the scan `
      + 'found none there, and a row is not invented above a run that has none. Tap the wall '
      + 'to put one up.')
  }

  /*
   * WHAT HAS A PIPE OR A GAS LINE UNDER IT, so the app can offer to lock it rather than
   * silently deciding either way.
   *
   * Every item here came from an actual matched scan object, never from `fill`'s plain
   * cabinets: a filler is only ever a plain cupboard (see `layOut` below), so a role in
   * SERVICE_ROLE surviving into the final runs is real -- the scan measured a sink, a
   * cooktop, an oven or a dishwasher standing at that position, today. That is the one
   * fact this whole module is in a position to know and a fresh "Design it for me" is not:
   * a generated layout is free to put the sink anywhere, and it is the plumber's bill, not
   * the software's, if it moves.
   */
  const services = []
  for (const run of runs) {
    for (const item of run.items) {
      if (item.gap || item.filler || !item.specId) continue
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      if (spec && SERVICE_ROLE.has(spec.role)) services.push({ id: item.id, role: spec.role, wallId: run.wallId })
    }
  }

  return { runs, islands, openings, counted: placed.length, notes, saw, services }
}

/** Roles worth asking about before a redesign is allowed to move them. */
const SERVICE_ROLE = new Set(['sink', 'cooktop', 'oven', 'dishwasher'])

/**
 * WHERE THE FLOOR IS, in the scan's own vertical numbers.
 *
 * RoomPlan's y = 0 is the phone's height at the instant somebody pressed start, not the
 * floor. In the owner's open-plan scan every benchtop came back at cy = -1029 and every
 * sofa at -1083, and a kitchen a metre underground is not what that means: the floor is at
 * -1508, and everything in this file that compares a height against a number -- bench
 * height, overhead height, a pantry -- is comparing against the wrong zero without this.
 *
 * The floor is where the furniture stands. Bucket every object's underside to the nearest
 * 100mm, take the fullest bucket (in the owner's scan, thirty of forty objects), and the
 * lowest underside in it is the floor. A payload that already counts from the floor -- every
 * fixture in the tests, and every older scan -- lands on zero and nothing changes.
 *
 * @param {Array} objects  already in the planner's frame
 */
function floorOf(objects) {
  const bottoms = (objects ?? []).filter(Boolean).map((o) => (o.cy ?? 0) - (o.height ?? 0) / 2)
  if (!bottoms.length) return 0
  const buckets = new Map()
  for (const b of bottoms) {
    const k = Math.round(b / 100)
    buckets.set(k, (buckets.get(k) ?? 0) + 1)
  }
  // The fullest, and the LOWEST of the fullest: a tie between the floor and the benchtop
  // band is a tie the floor wins, because a room has more things standing on it than on it.
  let best = null
  for (const [k, n] of buckets) if (!best || n > best.n || (n === best.n && k < best.k)) best = { k, n }
  return Math.min(...bottoms.filter((b) => Math.abs(b - best.k * 100) <= 150))
}

/**
 * Is this box a kitchen cupboard, or is it the television unit?
 *
 * RoomPlan calls a run of base cupboards, a pantry, a sideboard and a TV unit all the same
 * word -- `storage` -- and hands back no other opinion about them. What separates them is
 * HEIGHT: a kitchen is built to bench height or to full height and nothing in between, and
 * a piece of furniture is built to whatever is comfortable to reach across a room, which is
 * 650-700. In the owner's scan the two things that are not his kitchen -- the sideboard by
 * the fireplace and the unit under the television -- are the only two floor-standing
 * `storage` boxes whose tops come in under 800.
 *
 * @param {object} o      a scanned object, in the planner's frame
 * @param {number} floor  from `floorOf`
 */
function kitchenlike(o, floor) {
  const bottom = (o.cy ?? 0) - (o.height ?? 0) / 2 - floor
  const top = (o.cy ?? 0) + (o.height ?? 0) / 2 - floor
  // A row of overheads: it starts up in the band and there is nothing else up there.
  if (bottom >= OVERHEAD_AT) return true
  // A pantry or an oven tower: it reaches past every piece of furniture in a house.
  if (top >= TALL_TOP) return true
  return top >= BENCH_TOP[0] && top <= BENCH_TOP[1]
}

/** Evidence, strongest first, for what the note and the picking step say about a wall. */
const WHY_RANK = { bench: 3, service: 2, storage: 1 }

/**
 * WHICH WALLS ARE THE KITCHEN, as the scan would answer it before anybody is asked.
 *
 * THE PROBLEM THIS SOLVES. The owner scanned his kitchen and the scan came back with the
 * whole open-plan floor: twenty walls in one closed loop, the kitchen at one end and a
 * dining table, four sofas, a television and a fireplace at the other. Every wall of it is
 * real and the app has no business trimming any of them off -- that is the room, and the
 * doorways, the drawings and the 3D all have to keep showing it. But laying a kitchen out
 * against all twenty is how cupboards get proposed along a living-room wall.
 *
 * So the person is asked, on a plan they can put a finger on, and this is what comes up
 * TICKED. It has to be close to right or the step is worse than no step: a screen of twenty
 * walls all wrong is a screen nobody finishes.
 *
 * THE EVIDENCE, strongest first:
 *
 *   bench    a benchtop patch the LiDAR measured against that wall. Unarguable -- a
 *            horizontal surface at 900 in a house is a kitchen bench.
 *   service  a sink, a cooktop, an oven, a dishwasher or a fridge standing against it.
 *            RoomPlan's classifier is good at these, and each is a plumbing or a gas point.
 *   storage  a cupboard-shaped box against it, once the furniture has been ruled out by
 *            `kitchenlike` above.
 *
 * WHAT IS DELIBERATELY NOT APPLIED HERE. `fromScan` drops a low-confidence `storage` box,
 * because placing a cabinet somewhere on the strength of a guess costs somebody a deletion.
 * This is a far more forgiving question -- "may this wall carry cupboards", answered by a
 * finger in one tap either way -- and in the owner's scan nine of the eleven boxes against
 * his own kitchen walls came back low. Keeping them ticks five of his walls; dropping them
 * ticks three and leaves him to find the other two.
 *
 * @param {object} scan   the plugin's payload, in the phone's frame
 * @param {Array} walls   the loop from roomplan.loopFromSegments
 * @param {{x, y, angle}} origin  the frame that loop put the room in
 * @returns {{ walls: string[], why: Record<string, 'bench'|'service'|'storage'> }}
 *   `walls` in loop order, so the caller can show them the way the room reads.
 */
export function kitchenWalls(scan, walls, origin = { x: 0, y: 0 }) {
  const s = intoPlan(scan, origin)
  const why = {}
  const mark = (id, reason) => {
    if (!why[id] || WHY_RANK[reason] > WHY_RANK[why[id]]) why[id] = reason
  }

  // The same band and the same wall-matching `fromScan` measures a run with, so a wall that
  // gets a run is never a wall that came up unticked.
  const benches = (s?.benches ?? []).filter((b) => b && (b.length ?? 0) > 0
    && (!Number.isFinite(b.y) || (b.y >= BENCH_BAND[0] && b.y <= BENCH_BAND[1])))
  for (const id of alongWalls(benches, walls).spans.keys()) mark(id, 'bench')

  const objects = (s?.objects ?? []).filter(Boolean)
  const floor = floorOf(objects)
  for (const o of objects) {
    const role = roleOf(o.category)
    if (role === undefined) continue                    // a chair, a sofa, a fireplace
    if ((o.length ?? 0) < MIN_WIDTH) continue           // an artefact, whatever it is called
    // An appliance is a kitchen wherever it stands and however tall the scan made it -- a
    // cooktop reads 100mm high, because what the LiDAR saw of it was the glass.
    if (!role && !kitchenlike(o, floor)) continue
    const hit = nearestWall(o, walls)
    if (!hit || hit.gap > NEAR_WALL) continue           // out in the room: island, or nothing
    mark(hit.wall.id, role ? 'service' : 'storage')
  }

  return { walls: walls.filter((w) => why[w.id]).map((w) => w.id), why }
}

/**
 * Where the surfaces the scan measured sit along their walls, as one interval per wall.
 *
 * A benchtop is not against the wall the way `nearestWall` expects a cupboard to be -- it
 * is the front half of a cabinet, so its centre stands 280-600 off the wall line, and a
 * deep bench or an appliance garage further still. NEAR_WALL is the same line every other
 * scanned thing is judged by and it covers all of that; past it, the patch is not on a wall
 * at all and is handed back as island evidence.
 *
 * @param {Array} patches  benches or fronts, already in the planner's frame
 * @param {Array} walls
 * @returns {{ spans: Map<string, {from, to}>, adrift: Array }}
 */
function alongWalls(patches, walls) {
  const per = new Map()
  const adrift = []
  for (const p of patches) {
    const len = p.length ?? 0
    const hit = nearestWall(p, walls)
    if (!hit || hit.gap > NEAR_WALL) { adrift.push(p); continue }
    const d = room.dir(hit.wall)
    /*
     * The patch's own two ends, PROJECTED onto the wall's line rather than taken as
     * `at ± length / 2`. A patch has its own direction, and a benchtop that reads two or
     * three degrees off the wall it sits on is the ordinary case, not a fault; what a run
     * is measured in is its extent along the WALL.
     */
    const n = Math.hypot(p.dx ?? 0, p.dz ?? 0)
    const ux = n > 0.5 ? p.dx / n : d.x
    const uz = n > 0.5 ? p.dz / n : d.y
    const along = (x, z) => (x - hit.wall.x1) * d.x + (z - hit.wall.y1) * d.y
    const a = along(p.cx - ux * len / 2, p.cz - uz * len / 2)
    const b = along(p.cx + ux * len / 2, p.cz + uz * len / 2)
    const L = room.length(hit.wall)
    const from = Math.max(0, Math.min(a, b))
    const to = Math.min(L, Math.max(a, b))
    if (to - from < 1) continue
    if (!per.has(hit.wall.id)) per.set(hit.wall.id, [])
    per.get(hit.wall.id).push({ from, to })
  }

  const spans = new Map()
  for (const [wallId, list] of per) {
    const merged = []
    for (const iv of list.slice().sort((a, b) => a.from - b.from)) {
      const last = merged[merged.length - 1]
      if (last && iv.from <= last.to + BENCH_JOIN) last.to = Math.max(last.to, iv.to)
      else merged.push({ ...iv })
    }
    const real = merged.filter((iv) => iv.to - iv.from >= room.MIN_RUN)
    if (!real.length) continue
    /*
     * ONE INTERVAL PER WALL, from the start of the first stretch to the end of the last.
     *
     * A run is one contiguous list of cabinets whose positions are implied by their order
     * -- that is the shape the whole app is built on, and `room.extents` returns exactly
     * one interval per run for the same reason. So two stretches of bench with a real break
     * between them (a fridge, a doorway) become one run that covers both, and the break is
     * dealt with where every other break is: the appliance is a fixed point inside the run,
     * and the doorway is a gap `layOut` steps over.
     */
    spans.set(wallId, { from: real[0].from, to: real[real.length - 1].to })
  }
  return { spans, adrift }
}

/**
 * One run, laid out around what the scan found on it.
 *
 * The scanned things keep their measured positions; the gaps between them are filled with
 * the maker's plain cabinets by the same `fill` that fills a wall you tap on.
 *
 * `measured` means the run's own extent came off a surface the scan measured, in which case
 * an empty list is FILLED rather than left empty -- a benchtop with nothing named under it
 * is still 3.4 metres of cupboards, and that is the case this whole geometry pass exists
 * for. Without it, an empty list is left empty on purpose: the scene composes a run with
 * nothing in it, which is what a wall you tap on gets.
 */
function layOut(found, from, to, run, maker, seg, { plan, measured = false } = {}) {
  const lim = maker.limits
  const length = to - from
  if (length < 1 || !seg) return { items: [] }
  if (!found.length && !measured) return { items: [] }

  const plain = maker.cabinets.filter((c) => wantKind(c) === run.kind && !c.role
    && (run.kind === 'wall' ? c.kind === 'wall' : c.kind !== 'tall'))
  const pool = plain.length ? plain : maker.cabinets.filter((c) => wantKind(c) === run.kind && !c.role)
  if (!pool.length) return { items: [] }
  const smallest = Math.min(...pool.map((s) => s.stretch[0]))

  /*
   * A DOORWAY IN THE MIDDLE OF THE RUN IS A HOLE, NOT A CUPBOARD.
   *
   * `room.extents` has already dealt with an opening at either END of a wall by shortening
   * the run. What reaches here is the one in the middle, which cannot be answered by moving
   * a number -- and until now nothing here knew about openings at all, so a scan of the
   * owner's kitchen ran cabinets straight across the doorway beside the fridge.
   *
   * It becomes the same `{ specId: 'gap', gap: true }` item `design.js` emits from
   * `fillLeg`, which the renderer, the plan, the schedule, the price and `faults` all
   * already step over. It takes width, so the run still adds up exactly, and it never
   * moves: everything scanned is laid out around it.
   */
  const holes = []
  for (const o of room.blockedSpans(plan ?? { openings: [] }, run.wallId, run.kind, lim)) {
    const a = Math.max(from, o.from), b = Math.min(to, o.to)
    if (b - a < 1) continue
    holes.push({ pos: a, w: b - a, openingId: o.ids[0], gap: true, specId: 'gap' })
  }
  holes.sort((a, b) => a.pos - b.pos)
  const holeLen = holes.reduce((n, h) => n + h.w, 0)

  /*
   * Each scanned thing as a fixed block: the maker's nearest cabinet, at the position it was
   * measured, clipped into the run and shuffled clear of its neighbour.
   *
   * WHAT GIVES WAY WHEN THEY DO NOT FIT. A scan over-measures -- a run of doors reads wider
   * than the cabinets behind it, and the corner allowance takes 560 off the wall that the
   * tape never did -- so the blocks routinely total more than the run. The plain cupboards
   * go, widest first, and the sink and the appliances stay; dropping in scan order instead
   * loses whatever happens to be at the far end, which in the owner's kitchen was the
   * fridge and the oven. A doorway has taken its width off the run before any of this.
   */
  const blocks = []
  let dropped = 0
  for (const p of found.slice().sort((a, b) => a.at - b.at)) {
    const spec = pick(p, run.kind, maker)
    if (!spec) continue
    blocks.push({ specId: spec.id, spec, want: p.at, w: clampWidth(p.length, spec, lim) })
  }
  while (blocks.length > 1 && blocks.reduce((n, b) => n + b.w, 0) > length - holeLen) {
    let worst = -1, worstW = -1
    blocks.forEach((b, i) => {
      if (b.spec.role) return
      if (b.w > worstW) { worstW = b.w; worst = i }
    })
    // Everything left has a job and it still does not fit: stop, and let the clipping below
    // answer, rather than dropping a sink to make the arithmetic work.
    if (worst < 0) break
    blocks.splice(worst, 1)
    dropped++
  }

  const laid = []
  for (const b of blocks) {
    let pos = Math.max(from, Math.min(b.want - b.w / 2, to - b.w))
    const last = laid[laid.length - 1]
    if (last && pos < last.pos + last.w) pos = last.pos + last.w
    // Past any doorway it lands in. The holes are in order, so one pass settles it.
    for (const h of holes) if (pos < h.pos + h.w && pos + b.w > h.pos) pos = h.pos + h.w
    if (pos + b.w > to + 0.5) continue        // nothing left of the run to stand it in
    laid.push({ specId: b.specId, w: b.w, pos })
  }
  // Nothing could be stood in it and its length is nobody's measurement: left empty on
  // purpose, exactly as before, for the scene to compose.
  if (!laid.length && !measured) return { items: [] }

  const fixed = [...laid, ...holes].sort((a, b) => a.pos - b.pos)

  const items = []
  let stamp = 0
  const push = (it) => { items.push({ ...it, id: `${run.id}-${stamp++}` }); return items[items.length - 1] }
  let cursor = from
  let problem = null

  /*
   * The space before the next fixed block.
   *
   * A gap too small for any cabinet is given to whatever is beside it, within what the
   * maker will cut, and becomes a filler only when it cannot be -- which is what a maker
   * does: a 916 corner unit is a cabinet, a 16mm strip of board is not.
   */
  const gap = (len) => {
    if (len < 0.5) return
    if (len < smallest) {
      const prev = lastCabinet(items, maker)
      if (prev) {
        const headroom = Math.min(prev.spec.stretch[1], lim.maxCabinetWidth) - prev.item.w
        if (headroom >= len - 0.01) { prev.item.w += len; prev.item.stretched = true; return }
      }
      push({ specId: 'filler', w: len, filler: true })
      return
    }
    const r = fill(len, pool, lim)
    if (r.problem) problem = r.problem
    for (const it of r.items) push(it)
  }

  for (const f of fixed) {
    gap(f.pos - cursor)
    push(f.gap
      ? { specId: 'gap', gap: true, w: f.w, openingId: f.openingId }
      : { specId: f.specId, w: f.w })
    cursor = f.pos + f.w
  }
  gap(to - cursor)

  // The promise every other part of the app relies on: a run is exactly full. A doorway
  // counts toward that -- it is width the run spends -- but it is never the thing that
  // gives way, because its width is the door's, not ours.
  const over = sum(items) - length
  if (Math.abs(over) > 0.5) {
    if (over < 0) push({ specId: 'filler', w: -over, filler: true })
    else {
      const widest = items.filter((i) => !i.gap).sort((a, b) => b.w - a.w)[0]
      if (widest) widest.w -= over
    }
  }
  return { items, problem, dropped }
}

/**
 * The last real cabinet in the list, so a sliver can be given to something that stretches.
 *
 * Stops at a doorway. A cabinet on the far side of one may not grow across it: that would
 * move the hole, which is the one thing in the run whose position is not ours to choose.
 */
function lastCabinet(items, maker) {
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].gap) return null
    const spec = maker.cabinets.find((c) => c.id === items[i].specId)
    if (spec) return { spec, item: items[i] }
  }
  return null
}

/**
 * An island from whatever was standing away from the walls.
 *
 * `patches` are the good witnesses: a benchtop the scan measured out in the middle of the
 * room, or a single object long enough that nothing but an island is that shape. Either
 * carries a real length and a real direction, so the island is placed where it was measured
 * rather than fitted to a bounding box -- and one of them is enough, where the fallback
 * needs two things to have a box at all.
 *
 * ONE island, and only one. A room can hold an island and a dining table and a sideboard,
 * all of them big and all of them clear of the walls, and a kitchen with three islands in
 * it is a worse answer than a kitchen with none. So the candidates are ranked -- a measured
 * benchtop ahead of a classifier's guess, then whichever is nearest the cabinetry, then the
 * longest -- the first is made, and the count of the rest goes back for the note to say.
 */
function islandFrom(adrift, patches = [], kitchenAt = []) {
  const reach = (p) => (kitchenAt.length
    ? Math.min(...kitchenAt.map((k) => Math.hypot(p.cx - k.cx, p.cz - k.cz)))
    : 0)
  const ranked = patches.filter((p) => (p.length ?? 0) >= MIN_ISLAND)
    .map((p) => ({ p, near: reach(p) }))
    .sort((a, b) => (Number(!!b.p.fromBench) - Number(!!a.p.fromBench))
      || (a.near - b.near) || (b.p.length - a.p.length))
  const best = ranked[0]?.p
  if (best) {
    const len = Math.round(Math.min(best.length, 4000) / 10) * 10
    const n = Math.hypot(best.dx ?? 0, best.dz ?? 0)
    // `room.island` measures its angle the way `room.angle` does: screen y is down, so the
    // sign of the z component flips on the way in.
    const a = n > 0.5 ? Math.atan2(-best.dz / n, best.dx / n) : 0
    return {
      segment: room.island({ x: best.cx, y: best.cz }, a, len, { id: 'island-scan', overhang: 300 }),
      length: len,
      measured: true,
      others: ranked.length - 1,
    }
  }
  if (adrift.length < 2) return null
  const xs = adrift.map((p) => p.cx), zs = adrift.map((p) => p.cz)
  const spanX = Math.max(...xs) - Math.min(...xs)
  const spanZ = Math.max(...zs) - Math.min(...zs)
  // Along whichever axis the group is longer, plus a cabinet's width so the end ones fit.
  const along = Math.max(spanX, spanZ) + (adrift[0].length ?? 900)
  if (along < MIN_ISLAND) return null
  const centre = {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...zs) + Math.max(...zs)) / 2,
  }
  const len = Math.round(Math.min(along, 4000) / 10) * 10
  return {
    segment: room.island(centre, spanX >= spanZ ? 0 : Math.PI / 2, len,
      { id: 'island-scan', overhang: 300 }),
    length: len,
    measured: false,
    others: 0,
  }
}

/**
 * Everything the scan measured, one line each, in the planner's frame and in millimetres.
 *
 * The format is deliberately terse and deliberately regular: a leading word says what the
 * line is, and `scripts/scan-fixture.mjs` reads it back. It is meant to be pasted into a bug
 * report by somebody standing in their kitchen, so it has to survive a chat window -- no
 * punctuation that gets auto-corrected, no line that wraps into a different meaning.
 *
 *   wall <id> <x1>,<y1> <x2>,<y2>
 *   door|window wall=<id> at=<mm> w=<mm> sill=<mm> h=<mm>
 *   bench <cx>,<cz> <dx>,<dz> <len> <depth> <y>
 *   front <cx>,<cz> <dx>,<dz> <len> <depth> <y0> <y1>
 *   <category> <confidence> <cx>,<cz> <dx>,<dz> <len> <h> <depth>
 *
 * The object line leads with RoomPlan's own category, which is what a person reading the
 * paste wants to see first; nothing else in the file starts with one of those words.
 */
function geometryOf(scan, walls, openings) {
  const n = (v) => Math.round(Number(v) || 0)
  const d = (v) => Math.round((Number(v) || 0) * 1000) / 1000
  const out = []
  for (const w of walls) {
    out.push(`wall ${w.id} ${n(w.x1)},${n(w.y1)} ${n(w.x2)},${n(w.y2)}${w.bridged ? ' bridged' : ''}`)
  }
  for (const o of openings ?? []) {
    out.push(`${o.kind} wall=${o.wallId} at=${n(o.at)} w=${n(o.width)} sill=${n(o.sill)} h=${n(o.height)}`)
  }
  for (const b of scan?.benches ?? []) {
    out.push(`bench ${n(b.cx)},${n(b.cz)} ${d(b.dx)},${d(b.dz)} ${n(b.length)} ${n(b.depth)} ${n(b.y)}`)
  }
  for (const f of scan?.fronts ?? []) {
    out.push(`front ${n(f.cx)},${n(f.cz)} ${d(f.dx)},${d(f.dz)} ${n(f.length)} ${n(f.depth)} ${n(f.y0)} ${n(f.y1)}`)
  }
  for (const o of scan?.objects ?? []) {
    if (!o) continue
    out.push(`${String(o.category ?? '?').replace(/\s+/g, '')} ${String(o.confidence ?? '?')} `
      + `${n(o.cx)},${n(o.cz)} ${d(o.dx)},${d(o.dz)} ${n(o.length)} ${n(o.height)} ${n(o.depth)}`
      + ` cy=${n(o.cy)}`)
  }
  return out
}

/** How many of each, as `{name: n}`, for the line that says what the scan saw. */
function tally(names) {
  const out = {}
  for (const n of names) out[n] = (out[n] ?? 0) + 1
  return out
}

/**
 * Doors and windows, as openings on the wall they were found in.
 *
 * WIDTH IS NOT CAPPED, and that is deliberate. A kitchen opening onto a living room is a
 * two-and-a-half metre hole in a wall, RoomPlan reports it as exactly that, and a rule that
 * threw away anything wider than a door leaf would have thrown away the biggest hole in the
 * room. It is clamped to the WALL, though: an opening read a little wider than the wall it
 * was matched to would otherwise leave `at` negative and the run's arithmetic with it.
 */
function readOpenings(scan, walls) {
  const out = []
  const seen = { door: 0, window: 0 }

  /*
   * A WALL THAT IS THERE TO CARRY A HOLE.
   *
   * `loopFromSegments` bridges a gap between two wall pieces that line up, because that is
   * what a wide archway looks like to RoomPlan when it does not report an `opening` for it:
   * the plaster stops, and starts again two metres later. The bridge closes the room -- it
   * used to refuse to close at all -- and this is the other half of it: the whole of that
   * bridging wall is a doorway, floor to ceiling, so nothing is ever laid against it.
   */
  for (const w of walls) {
    if (!w.bridged) continue
    const width = Math.round(room.length(w) / 10) * 10
    if (width < 400) continue
    out.push({ id: `o-door-${seen.door++}`, wallId: w.id, kind: 'door', at: 0, width,
      sill: 0, height: 2400 })
  }

  for (const [kind, list] of [['door', scan?.doors], ['window', scan?.windows], ['door', scan?.openings]]) {
    for (const s of list ?? []) {
      const hit = nearestWall(s, walls)
      /*
       * A DOORWAY IS ALLOWED TO BE FURTHER OFF THAN A CUPBOARD IS.
       *
       * `loopFromSegments` builds the room from the wall pieces RoomPlan returned, and a
       * wide cased opening is not a wall -- so the pieces either side of it are two
       * segments and the opening's centre can land most of a metre past the end of both.
       * At the cupboard's tolerance it was dropped, and the planner then ran a benchtop
       * straight across the opening, which is what it looked like in the room. An opening
       * is IN a wall by definition; the only question is which one.
       */
      if (!hit || hit.gap > NEAR_OPENING) continue
      const L = room.length(hit.wall)
      // Clamped into its wall, both ends. A cased opening reads a little wider than the wall
      // segment it was matched to often enough to matter, and `at = L - width` then goes
      // negative -- which is how a doorway ended up starting before the wall did and every
      // run on that wall came out longer than the wall.
      const width = Math.min(Math.round(s.length / 10) * 10, Math.round(L / 10) * 10)
      if (width < 400) continue
      const height = Math.round((s.height ?? (kind === 'door' ? 2040 : 1200)) / 10) * 10
      // `cy` is the centre's height, so the sill is half the height below it. A doorway that
      // reads as starting slightly below zero is one standing on the floor.
      const sill = Math.max(0, Math.round(((s.cy ?? height / 2) - height / 2) / 10) * 10)
      const at = Math.max(0, Math.min(Math.round((hit.at - width / 2) / 10) * 10, L - width))
      // A wall already given over entirely to a bridged doorway is not given a second one.
      if (hit.wall.bridged) continue
      out.push({ id: `o-${kind}-${seen[kind]++}`, wallId: hit.wall.id, kind, at, width, sill, height })
    }
  }
  return out
}

/**
 * The wall an object stands against, how far along it, and how far off it.
 *
 * Projected onto each wall's own line: `at` is the distance from the wall's start to the
 * object's centre, `gap` how far the centre is from the wall. A cupboard 280 off the wall is
 * against it (it is half its own depth away); one 2000 off is an island.
 */
function nearestWall(o, walls) {
  let best = null
  for (const wall of walls) {
    const d = room.dir(wall), n = room.inward(wall)
    const vx = o.cx - wall.x1, vy = o.cz - wall.y1
    const at = vx * d.x + vy * d.y
    const off = vx * n.x + vy * n.y
    const L = room.length(wall)
    // Outside the wall's own span counts as further away, so a cupboard past the end of one
    // wall belongs to the wall it is actually on.
    const past = Math.max(0, -at, at - L)
    const gap = Math.hypot(Math.max(0, off), past)
    if (off < -200) continue          // behind the wall, so not in this room
    if (!best || gap < best.gap) best = { wall, at, gap }
  }
  return best
}

/** Base or overhead, from how high off the floor the thing starts. */
function kindOf(p) {
  const bottom = (p.cy ?? 0) - (p.height ?? 0) / 2
  if (bottom > OVERHEAD_AT) return 'wall'
  // A pantry or a fridge is floor-standing and tall; it lives in the base run, as it does
  // everywhere else in this app.
  return 'base'
}

const wantKind = (spec) => (spec.kind === 'wall' ? 'wall' : 'base')

/** The maker's nearest cabinet to what was scanned. */
function pick(p, kind, maker) {
  const pool = maker.cabinets.filter((c) => wantKind(c) === kind)
  if (!pool.length) return null
  if (p.role) {
    const byRole = pool.find((c) => c.role === p.role)
    if (byRole) return byRole
  }
  // A tall thing standing on the floor is a pantry, not a base cabinet.
  const tall = (p.height ?? 0) > TALL_AT && kindOf(p) === 'base'
  const shortlist = pool.filter((c) => (tall ? c.kind === 'tall' : c.kind !== 'tall') && !c.role)
  const from = shortlist.length ? shortlist : pool.filter((c) => !c.role)
  if (!from.length) return pool[0]
  const w = near(p.length, from.map((c) => c.w))
  return from.find((c) => c.w === w) ?? from[0]
}

/** The scanned width, clamped into what the maker will actually cut. */
const clampWidth = (mm_, spec, lim) => Math.round(
  Math.min(Math.max(mm_, spec.stretch[0]), Math.min(spec.stretch[1], lim.maxCabinetWidth)))

/** The four the shape ids name, in the words the rest of the app uses. */
const PLAIN = { back: 'the back wall', left: 'the left wall', right: 'the right wall',
  front: 'the facing wall' }

/**
 * A wall in words somebody can find in their own kitchen.
 *
 * "Wall w2" is a variable name, and it went out in notes people read: "Wall w2 and Wall w3
 * got no overhead cupboards" tells a homeowner nothing about which two walls those are. Four
 * of the walls in a squared-up room have real names; a fifth, angled one has none, so it is
 * described by the two it stands between -- "the wall between the back wall and the left
 * wall" -- which is how a person would point at it.
 *
 * @param {string} id
 * @param {boolean} isle
 * @param {Array} walls  the loop, so an unnamed wall can be described by its neighbours
 */
function wallName(id, isle, walls) {
  if (isle) return 'The island'
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)
  if (PLAIN[id]) return cap(PLAIN[id])
  const w = (walls ?? []).find((x) => x.id === id)
  if (w) {
    const before = PLAIN[room.prev(walls, w)?.id]
    const after = PLAIN[room.next(walls, w)?.id]
    if (before && after) return `The wall between ${before} and ${after}`
    if (before) return `The wall after ${before}`
    if (after) return `The wall before ${after}`
  }
  return `Wall ${id}`
}
